import { useMemo } from 'react';
import { useRouter } from 'next/compat/router';
import type { ApolloError } from '@apollo/client';

import { consumerDispensaries } from 'shared/graphql/dispensary/queries';
import { useQuery } from 'hooks/use-query';
import { getVariables } from 'src/dispensary/hooks/use-dispensary';
import { useCollectionChannel } from 'src/hooks/use-collection-channel';
import { computeSkip } from 'src/dispensary/context/storefront-bootstrap-context';
import { useEnterpriseStorefrontContext } from 'src/dispensary/enterprise-storefront-context';
import type { Dispensaries } from 'src/gql/graphql';

type ConsumerDispensariesData = {
  filteredDispensaries?: Dispensaries[] | null;
};

export type UseCurrentDispensaryResult = {
  dispensary: Dispensaries | null;
  loading: boolean;
  error?: ApolloError | Error;
};

export function useCurrentDispensary(): UseCurrentDispensaryResult {
  const router = useRouter();
  const enterpriseStorefront = useEnterpriseStorefrontContext();
  const { cName, cname, dispensaryId: dispoIdFromQuery } = (router?.query ?? {}) as {
    cName?: string;
    cname?: string;
    dispensaryId?: string;
  };
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const dispensaryId = cName || dispoIdFromQuery || cname || enterpriseStorefront?.dispensaryId;

  const collectionChannel = useCollectionChannel();

  const { data, loading, error } = useQuery<ConsumerDispensariesData, ReturnType<typeof getVariables>>(
    consumerDispensaries,
    {
      variables: getVariables(dispensaryId, undefined, collectionChannel),
      skip: computeSkip(router?.isReady, typeof window !== 'undefined', dispensaryId),
    }
  );

  const dispensary = useMemo<Dispensaries | null>(() => {
    if (!dispensaryId) {
      return null;
    }
    const results = data?.filteredDispensaries ?? [];
    return (
      results.find((candidate) => candidate.cName === dispensaryId || String(candidate.id) === String(dispensaryId)) ??
      null
    );
  }, [data, dispensaryId]);

  return {
    dispensary,
    loading,
    error,
  };
}

export default useCurrentDispensary;
